
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useNavigate } from 'react-router-dom';

interface Product {
  id: number;
  name: string;
  category: string;
  origin: string;
  packaging: string;
  description: string;
  image: string;
  certifications: string[];
  specifications: {
    shelfLife: string;
    storage: string;
    packaging: string;
  };
  rotation?: number;
  offsetX?: number;
  offsetY?: number;
}

export default function Products() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  const categories = [
    { id: 'all', name: 'All Products', icon: 'ri-grid-line' },
    { id: 'frozen-fruits', name: 'Frozen Fruits', icon: 'ri-apple-line' },
    { id: 'frozen-vegetables', name: 'Frozen Vegetables', icon: 'ri-leaf-line' },
    { id: 'freeze-dried', name: 'Freeze-Dried', icon: 'ri-temp-cold-line' },
    { id: 'purees', name: 'Purees & Concentrates', icon: 'ri-drop-line' },
    { id: 'juices', name: 'Juices', icon: 'ri-cup-line' },
    { id: 'herbs', name: 'Herbs & Spices', icon: 'ri-plant-line' }
  ];

  const products: Product[] = [
    {
      id: 1,
      name: 'IQF Strawberries',
      category: 'frozen-fruits',
      origin: 'Turkey',
      packaging: '10kg, 25kg bags',
      description: 'Premium individually quick frozen strawberries with excellent color and flavor retention.',
      image: 'https://readdy.ai/api/search-image?query=premium%20frozen%20strawberries%20in%20professional%20packaging%2C%20individual%20quick%20frozen%20berries%2C%20bright%20red%20color%2C%20commercial%20food%20display%2C%20clean%20white%20background&width=400&height=300&seq=9&orientation=landscape',
      certifications: ['HACCP', 'ISO 22000', 'BRC'],
      specifications: {
        shelfLife: '24 months',
        storage: '-18°C',
        packaging: 'PE bags in cartons'
      }
    },
    {
      id: 2,
      name: 'Frozen Mixed Berries',
      category: 'frozen-fruits',
      origin: 'Turkey',
      packaging: '1kg, 10kg bags',
      description: 'Blend of strawberries, blackberries, blueberries, and raspberries.',
      image: 'https://readdy.ai/api/search-image?query=mixed%20frozen%20berries%20blend%20strawberries%20blueberries%20blackberries%20in%20professional%20packaging%2C%20colorful%20assorted%20berries%2C%20premium%20quality%20display&width=400&height=300&seq=10&orientation=landscape',
      certifications: ['HACCP', 'ISO 22000', 'Organic'],
      specifications: {
        shelfLife: '24 months',
        storage: '-18°C',
        packaging: 'PE bags in cartons'
      }
    },
    {
      id: 3,
      name: 'IQF Broccoli Florets',
      category: 'frozen-vegetables',
      origin: 'Turkey',
      packaging: '10kg, 25kg bags',
      description: 'Fresh green broccoli florets individually quick frozen to preserve nutrients.',
      image: 'https://readdy.ai/api/search-image?query=frozen%20broccoli%20florets%20in%20professional%20packaging%2C%20bright%20green%20vegetables%2C%20individual%20quick%20frozen%2C%20commercial%20food%20display%2C%20clean%20presentation&width=400&height=300&seq=11&orientation=landscape',
      certifications: ['HACCP', 'ISO 22000', 'BRC'],
      specifications: {
        shelfLife: '24 months',
        storage: '-18°C',
        packaging: 'PE bags in cartons'
      }
    },
    {
      id: 4,
      name: 'Freeze-Dried Strawberry Powder',
      category: 'freeze-dried',
      origin: 'Turkey',
      packaging: '5kg, 20kg drums',
      description: 'Pure freeze-dried strawberry powder with intense flavor and color.',
      image: 'https://readdy.ai/api/search-image?query=freeze-dried%20strawberry%20powder%20in%20industrial%20containers%2C%20vibrant%20red%20powder%2C%20professional%20food%20processing%2C%20clean%20modern%20facility&width=400&height=300&seq=12&orientation=landscape',
      certifications: ['HACCP', 'ISO 22000', 'Organic'],
      specifications: {
        shelfLife: '36 months',
        storage: 'Cool, dry place',
        packaging: 'PE lined drums'
      }
    },
    {
      id: 5,
      name: 'Apple Puree Concentrate',
      category: 'purees',
      origin: 'Turkey',
      packaging: '20kg, 200kg drums',
      description: 'Natural apple puree concentrate without additives or preservatives.',
      image: 'https://readdy.ai/api/search-image?query=apple%20puree%20concentrate%20in%20industrial%20drums%2C%20natural%20apple%20color%2C%20food%20processing%20facility%2C%20professional%20product%20display&width=400&height=300&seq=13&orientation=landscape',
      certifications: ['HACCP', 'ISO 22000', 'BRC'],
      specifications: {
        shelfLife: '24 months',
        storage: 'Cool, dry place',
        packaging: 'Aseptic drums'
      }
    },
    {
      id: 6,
      name: 'Pomegranate Juice Concentrate',
      category: 'juices',
      origin: 'Turkey',
      packaging: '5kg, 25kg containers',
      description: 'Premium pomegranate juice concentrate with high antioxidant content.',
      image: 'https://readdy.ai/api/search-image?query=pomegranate%20juice%20concentrate%20in%20professional%20containers%2C%20deep%20red%20color%2C%20premium%20beverage%20processing%2C%20modern%20facility&width=400&height=300&seq=14&orientation=landscape',
      certifications: ['HACCP', 'ISO 22000', 'Organic'],
      specifications: {
        shelfLife: '24 months',
        storage: 'Cool, dry place',
        packaging: 'Aseptic containers'
      }
    },
    {
      id: 7,
      name: 'Dried Oregano',
      category: 'herbs',
      origin: 'Turkey',
      packaging: '100g, 1kg, 25kg bags',
      description: 'Premium quality dried oregano with intense aroma and flavor.',
      image: 'https://readdy.ai/api/search-image?query=dried%20oregano%20herbs%20in%20professional%20packaging%2C%20natural%20green%20color%2C%20aromatic%20spices%2C%20commercial%20herb%20processing&width=400&height=300&seq=15&orientation=landscape',
      certifications: ['HACCP', 'ISO 22000', 'Organic'],
      specifications: {
        shelfLife: '24 months',
        storage: 'Cool, dry place',
        packaging: 'PE bags'
      }
    },
    {
      id: 8,
      name: 'IQF Green Peas',
      category: 'frozen-vegetables',
      origin: 'Turkey',
      packaging: '10kg, 25kg bags',
      description: 'Sweet green peas individually quick frozen at peak freshness.',
      image: 'https://readdy.ai/api/search-image?query=frozen%20green%20peas%20in%20professional%20packaging%2C%20bright%20green%20color%2C%20individual%20quick%20frozen%20vegetables%2C%20commercial%20food%20display&width=400&height=300&seq=16&orientation=landscape',
      certifications: ['HACCP', 'ISO 22000', 'BRC'],
      specifications: {
        shelfLife: '24 months',
        storage: '-18°C',
        packaging: 'PE bags in cartons'
      }
    }
  ];

  // Generate random rotations and offsets for each product
  const generateCardTransforms = (products: Product[]) => {
    return products.map(product => ({
      ...product,
      rotation: Math.random() * 12 - 6, // Random rotation between -6deg and +6deg
      offsetX: Math.random() * 20 - 10, // Random X offset between -10px and +10px
      offsetY: Math.random() * 15 - 7.5, // Random Y offset between -7.5px and +7.5px
    }));
  };

  useEffect(() => {
    let filtered = products;

    if (activeCategory !== 'all') {
      filtered = filtered.filter(product => product.category === activeCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply random transforms to filtered products
    setFilteredProducts(generateCardTransforms(filtered));
  }, [activeCategory, searchTerm]);

  // Card fall animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: -100, 
      rotate: Math.random() * 60 - 30,
      scale: 0.8
    },
    visible: (custom: { rotation: number; offsetX: number; offsetY: number; delay: number }) => ({
      opacity: 1,
      y: custom.offsetY,
      x: custom.offsetX,
      rotate: custom.rotation,
      scale: 1,
      transition: {
        duration: 0.8,
        delay: custom.delay,
        ease: [0.25, 0.46, 0.45, 0.94],
        type: "spring",
        damping: 15,
        stiffness: 100
      }
    }),
    hover: {
      rotate: 0,
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-r from-primary-600 to-green-600">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Premium Export Products
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed">
              Discover our comprehensive range of frozen fruits, vegetables, and processed products 
              exported to 50+ countries worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-12 bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-sm"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer text-sm ${
                    activeCategory === category.id
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  <i className={category.icon}></i>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Scattered Cards */}
      <section className="py-16 overflow-hidden" data-product-shop>
        <div className="container mx-auto px-4">
          <AnimatePresence mode="wait">
            <div className="relative">
              {/* Scattered Cards Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-12">
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    custom={{
                      rotation: product.rotation || 0,
                      offsetX: product.offsetX || 0,
                      offsetY: product.offsetY || 0,
                      delay: index * 0.1
                    }}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    whileHover="hover"
                    className="relative cursor-pointer group"
                    style={{
                      transformOrigin: 'center center',
                      zIndex: 1
                    }}
                    whileInView={{ 
                      opacity: 1,
                      transition: { delay: index * 0.05 }
                    }}
                    viewport={{ once: true, margin: "-50px" }}
                    onClick={() => navigate(`/products/${product.id}`)}
                  >
                    {/* Card Shadow */}
                    <motion.div
                      className="absolute inset-0 bg-black/20 rounded-2xl blur-md"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.1 }}
                      whileHover={{ opacity: 0.25, scale: 1.05 }}
                      style={{
                        transform: `translate(${(product.offsetX || 0) * 0.5}px, ${((product.offsetY || 0) * 0.5) + 8}px)`
                      }}
                    />
                    
                    {/* Main Card */}
                    <div className="relative bg-white rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
                      {/* Product Image */}
                      <div className="relative">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="bg-primary-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                            {product.origin}
                          </span>
                        </div>
                        
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>

                      {/* Product Info */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">
                          {product.name}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {product.description}
                        </p>

                        {/* Specifications */}
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm">
                            <i className="ri-package-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                            <span className="text-gray-600">{product.packaging}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <i className="ri-time-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                            <span className="text-gray-600">{product.specifications.shelfLife}</span>
                          </div>
                        </div>

                        {/* Certifications */}
                        <div className="flex flex-wrap gap-1 mb-4">
                          {product.certifications.map((cert, index) => (
                            <span
                              key={index}
                              className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium"
                            >
                              {cert}
                            </span>
                          ))}
                        </div>

                        {/* CTA */}
                        <Button 
                          size="sm" 
                          className="w-full"
                          onClick={() => {
                            navigate(`/products/${product.id}`);
                          }}
                        >
                          View Details
                          <i className="ri-arrow-right-line ml-2 w-4 h-4 flex items-center justify-center"></i>
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </AnimatePresence>

          {filteredProducts.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <i className="ri-search-line text-6xl text-gray-300 mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
              <h3 className="text-2xl font-semibold text-gray-600 mb-2">
                No products found
              </h3>
              <p className="text-gray-500 mb-6">
                Try adjusting your search or filter criteria
              </p>
              <Button onClick={() => { setSearchTerm(''); setActiveCategory('all'); }}>
                Clear Filters
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Need Custom Products or Bulk Orders?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Contact our export specialists for custom formulations, private labeling, 
              and bulk order solutions tailored to your market needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-primary-600 hover:bg-primary-700"
              >
                Request Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => navigate('/services')}
                className="border-white text-white hover:bg-white hover:text-gray-900"
              >
                Our Services
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
